/**
 * Settings: the network's lms_settings form (plan docs/plans/lms-plan.md 5.8,
 * 9.1 to 9.3, Appendix A "Settings and jobs", Appendix F; 18.9, 18.26, 18.27;
 * WP-48; R4, R23). Three cards:
 *
 * - Organization and certificates: organization_name (printed on every
 *   certificate and in every email footer, non-blank, at most 200) and the
 *   signer's name and title (at most 120 each; empty prints the organization
 *   alone, Q6).
 * - Reminders and the weekly digest: the time zone the hourly tick keeps
 *   (9.1), the due_soon thresholds (9.2; descending whole numbers 1 to 90, at
 *   least one, 18.27), the overdue repeat (1 to 365), the digest hour (0 to
 *   23) and weekday (ISO 1 to 7), and the extra digest recipients beyond the
 *   lms admins (9.3; at most 50 people the admin reaches, WP-43's UserPicker
 *   over the directory, 18.26).
 * - Defaults: a new course's renewal lead (0 to 365) and pass mark (0 to 100)
 *   and a new assignment's due window (1 to 365).
 *
 * Save sends ONLY the changed fields (PUT /admin/settings), each checked first
 * with the route's own rules (formModel.ts); a field that fails shows its
 * message beside it and nothing is sent. A 400 from the server (it re-checks
 * everything and can refuse a digest recipient out of reach) shows its
 * message at the top and marks the field it names. The saved row the server
 * answers becomes the form's new starting point; every outcome is toasted.
 */
import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Save, Undo2 } from 'lucide-react';
import { fieldCls, primaryBtnCls, secondaryBtnCls } from '../../../../ui/styles';
import { useToast } from '../../../../ui/Toast';
import { apiErrorCode, apiErrorMessage } from '../../../api/client';
import { updateSettings } from '../../../api/settings';
import type { LmsSettings, SettingsField } from '../../../api/settings';
import { UserPicker } from '../../../components/pickers/UserPicker';
import { cardCls, ErrorNote, labelCls, metaCls } from '../../../components/pickers/shared';
import {
  buildPatch,
  changedFields,
  COMMON_TIME_ZONES,
  FIELD_LABELS,
  fieldFromServerMessage,
  formFromSettings,
  hourLabel,
  LIMITS,
  WEEKDAY_LABELS,
} from './formModel';
import type { SettingsErrors, SettingsFormState } from './formModel';
import { cardTitleCls, fieldErrorCls, formatInstant, hintCls } from './shared';

export interface SettingsFormProps {
  settings: LmsSettings;
  onSaved: (settings: LmsSettings) => void;
}

type TextField = Exclude<SettingsField, 'admin_digest_user_ids'>;

const inputCls = `${fieldCls} w-full min-h-[44px] sm:min-h-0`;
const invalidCls = 'border-red-500 dark:border-red-500';

function Field({
  field,
  hint,
  error,
  children,
}: {
  field: SettingsField;
  hint?: ReactNode;
  error?: string;
  children: ReactNode;
}) {
  return (
    <div data-testid={`lms-setting-row-${field}`}>
      <label htmlFor={`lms-setting-${field}`} className={labelCls}>
        {FIELD_LABELS[field]}
      </label>
      {children}
      {error ? (
        <p id={`lms-setting-${field}-error`} className={fieldErrorCls} data-testid={`lms-setting-error-${field}`}>
          {error}
        </p>
      ) : hint ? (
        <p id={`lms-setting-${field}-hint`} className={hintCls}>
          {hint}
        </p>
      ) : null}
    </div>
  );
}

export function SettingsForm({ settings, onSaved }: SettingsFormProps) {
  const { toast } = useToast();
  const initial = useMemo(() => formFromSettings(settings), [settings]);
  const [form, setForm] = useState<SettingsFormState>(initial);
  const [baseline, setBaseline] = useState<SettingsFormState>(initial);
  const [errors, setErrors] = useState<SettingsErrors>({});
  const [serverError, setServerError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // A new row from the host (a reload) restarts the form from it.
  if (baseline !== initial && !saving) {
    setBaseline(initial);
    setForm(initial);
    setErrors({});
  }

  const changed = changedFields(form, baseline);
  const dirty = changed.length > 0;
  const invalidCount = Object.keys(errors).length;

  const set = (field: TextField, value: string) => {
    setForm((f) => ({ ...f, [field]: value }));
    if (errors[field]) {
      setErrors((e) => {
        const next = { ...e };
        delete next[field];
        return next;
      });
    }
  };

  const setRecipients = (ids: string[]) => {
    setForm((f) => ({ ...f, admin_digest_user_ids: ids }));
    if (errors.admin_digest_user_ids) {
      setErrors((e) => {
        const next = { ...e };
        delete next.admin_digest_user_ids;
        return next;
      });
    }
  };

  const discard = () => {
    setForm(baseline);
    setErrors({});
    setServerError(null);
  };

  const save = async () => {
    if (saving) return;
    const { patch, errors: found } = buildPatch(form, baseline);
    setErrors(found);
    setServerError(null);
    if (!patch) return;
    setSaving(true);
    try {
      const saved = await updateSettings(patch);
      toast('Settings saved.');
      onSaved(saved);
    } catch (err) {
      const message = apiErrorMessage(err, 'The settings could not be saved.');
      setServerError(message);
      const field = apiErrorCode(err) === 'LMS_INVALID' ? fieldFromServerMessage(message) : null;
      if (field) setErrors((e) => ({ ...e, [field]: message }));
      toast(message, { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const describedBy = (field: SettingsField, hasHint: boolean) =>
    errors[field] ? `lms-setting-${field}-error` : hasHint ? `lms-setting-${field}-hint` : undefined;

  const textInput = (field: TextField, opts: { hint?: ReactNode; numeric?: boolean; placeholder?: string } = {}) => (
    <Field field={field} hint={opts.hint} error={errors[field]}>
      <input
        id={`lms-setting-${field}`}
        type="text"
        inputMode={opts.numeric ? 'numeric' : undefined}
        className={`${inputCls} ${errors[field] ? invalidCls : ''}`}
        value={form[field]}
        onChange={(e) => set(field, e.target.value)}
        placeholder={opts.placeholder}
        aria-invalid={errors[field] ? true : undefined}
        aria-describedby={describedBy(field, Boolean(opts.hint))}
        data-testid={`lms-setting-${field}`}
      />
    </Field>
  );

  const zones = COMMON_TIME_ZONES.includes(form.timezone) || !form.timezone ? COMMON_TIME_ZONES : [form.timezone, ...COMMON_TIME_ZONES];
  const updatedAt = formatInstant(settings.updated_at ?? null);
  let stateText = updatedAt ? `Saved. Last changed ${updatedAt}.` : 'Saved.';
  if (dirty) stateText = changed.length === 1 ? 'One unsaved change.' : `${changed.length} unsaved changes.`;

  return (
    <form
      className="space-y-4"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
      data-testid="lms-settings-form"
    >
      {invalidCount > 0 && !serverError ? (
        <ErrorNote testId="lms-settings-invalid">
          {invalidCount === 1 ? "One setting needs a fix before it can be saved." : `${invalidCount} settings need a fix before they can be saved.`}
        </ErrorNote>
      ) : null}
      {serverError ? <ErrorNote testId="lms-settings-save-error">{serverError}</ErrorNote> : null}

      <section className={cardCls} aria-labelledby="lms-settings-org-title">
        <h2 id="lms-settings-org-title" className={cardTitleCls}>
          Organization and certificates
        </h2>
        <div className="mt-3 grid gap-4 md:grid-cols-2 2xl:grid-cols-3">
          {textInput('organization_name', {
            hint: "Printed on every certificate and at the foot of every Learning Center email.",
          })}
          {textInput('certificate_signer_name', {
            hint: "Printed as the signer on certificates. Leave it empty to print the organization alone.",
          })}
          {textInput('certificate_signer_title', { hint: "For example Director of Clinical Operations." })}
        </div>
      </section>

      <section className={cardCls} aria-labelledby="lms-settings-reminders-title">
        <h2 id="lms-settings-reminders-title" className={cardTitleCls}>
          Reminders and the weekly digest
        </h2>
        <div className="mt-3 grid gap-4 md:grid-cols-2 2xl:grid-cols-3">
          <Field field="timezone" hint="The local day and hour the reminders and the digest follow." error={errors.timezone}>
            <select
              id="lms-setting-timezone"
              className={`${inputCls} ${errors.timezone ? invalidCls : ''}`}
              value={form.timezone}
              onChange={(e) => set('timezone', e.target.value)}
              aria-invalid={errors.timezone ? true : undefined}
              aria-describedby={describedBy('timezone', true)}
              data-testid="lms-setting-timezone"
            >
              {zones.map((z) => (
                <option key={z} value={z}>
                  {z}
                </option>
              ))}
            </select>
          </Field>
          {textInput('reminder_days', {
            hint: "Days before the due date a reminder goes out, largest first, 1 to 90 (for example 30, 14, 7, 1).",
            placeholder: '30, 14, 7, 1',
          })}
          {textInput('overdue_repeat_days', {
            numeric: true,
            hint: "An overdue reminder repeats every this many days, 1 to 365.",
          })}
          <Field field="digest_hour_local" hint="The daily pass and the digest run from this local hour." error={errors.digest_hour_local}>
            <select
              id="lms-setting-digest_hour_local"
              className={`${inputCls} ${errors.digest_hour_local ? invalidCls : ''}`}
              value={form.digest_hour_local}
              onChange={(e) => set('digest_hour_local', e.target.value)}
              aria-invalid={errors.digest_hour_local ? true : undefined}
              aria-describedby={describedBy('digest_hour_local', true)}
              data-testid="lms-setting-digest_hour_local"
            >
              {Array.from({ length: 24 }, (_, h) => (
                <option key={h} value={String(h)}>
                  {hourLabel(h)}
                </option>
              ))}
            </select>
          </Field>
          <Field field="admin_digest_weekday" hint="The weekly digest goes out on this day." error={errors.admin_digest_weekday}>
            <select
              id="lms-setting-admin_digest_weekday"
              className={`${inputCls} ${errors.admin_digest_weekday ? invalidCls : ''}`}
              value={form.admin_digest_weekday}
              onChange={(e) => set('admin_digest_weekday', e.target.value)}
              aria-invalid={errors.admin_digest_weekday ? true : undefined}
              aria-describedby={describedBy('admin_digest_weekday', true)}
              data-testid="lms-setting-admin_digest_weekday"
            >
              {Object.entries(WEEKDAY_LABELS).map(([n, label]) => (
                <option key={n} value={n}>
                  {label}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <div className="mt-4" data-testid="lms-setting-row-admin_digest_user_ids">
          <UserPicker
            value={form.admin_digest_user_ids}
            onChange={(ids) => setRecipients(ids)}
            label={FIELD_LABELS.admin_digest_user_ids}
            disabled={saving}
          />
          {errors.admin_digest_user_ids ? (
            <p className={fieldErrorCls} data-testid="lms-setting-error-admin_digest_user_ids">
              {errors.admin_digest_user_ids}
            </p>
          ) : (
            <p className={hintCls}>
              {`Every Learning Center admin gets the digest for the sites they manage. Add up to ${LIMITS.digestRecipientsMax} more people here; each one gets the digest for the sites they reach.`}
            </p>
          )}
        </div>
      </section>

      <section className={cardCls} aria-labelledby="lms-settings-defaults-title">
        <h2 id="lms-settings-defaults-title" className={cardTitleCls}>
          Defaults
        </h2>
        <div className="mt-3 grid gap-4 md:grid-cols-2 2xl:grid-cols-3">
          {textInput('renewal_lead_days_default', {
            numeric: true,
            hint: "A new course assigns its renewal this many days before a completion expires, 0 to 365.",
          })}
          {textInput('default_due_days', {
            numeric: true,
            hint: "A new assignment is due this many days after it is sent, 1 to 365.",
          })}
          {textInput('default_pass_mark_pct', {
            numeric: true,
            hint: "The quiz pass mark a new course starts with, as a percentage from 0 to 100.",
          })}
        </div>
      </section>

      <div className="flex flex-wrap items-center gap-2">
        <button type="submit" className={primaryBtnCls} disabled={!dirty || saving} data-testid="lms-settings-save">
          <Save className="h-4 w-4" aria-hidden="true" />
          {saving ? 'Saving...' : 'Save settings'}
        </button>
        <button type="button" className={secondaryBtnCls} onClick={discard} disabled={!dirty || saving} data-testid="lms-settings-discard">
          <Undo2 className="h-4 w-4" aria-hidden="true" />
          Discard changes
        </button>
        <span className={metaCls} aria-live="polite" data-testid="lms-settings-state">
          {stateText}
        </span>
      </div>
    </form>
  );
}

export default SettingsForm;
